import { AppData, Colors, Lightning } from '@lightningjs/sdk';
import Page from 'components/Page';
import { PageId } from 'types/pageId';
import { translate } from 'support/translate';
import { IndexData, LightningBuilder } from 'types/lightning';
import { ListWithSpeech } from 'components/common/CollectionWrappersWithSpeech';
import ListSelection, {
  ListSelectionTemplateSpec,
} from 'components/pages/settings/ListSelection';
import SettingsSection from 'components/pages/settings/SettingsSection';
import { getFontFaceFromStyle } from 'support/textUtils';
import LoggedOutProfile from 'components/pages/settings/profiles/LoggedOutProfile';
import LoggedInProfile from 'components/pages/settings/profiles/LoggedInProfile';
import SignOutButton from 'components/pages/settings/profiles/SignOutButton';
import TextLabel from 'components/common/TextLabel';

export interface SettingsPageTemplateSpec
  extends Lightning.Component.TemplateSpec {
  Content: {
    Header: typeof TextLabel;
    Settings: {
      Left: {
        LoggedOutProfile: typeof LoggedOutProfile;
        LoggedInProfile: typeof LoggedInProfile;
        SectionsList: typeof ListWithSpeech;
        SignOutButton: typeof SignOutButton;
      };

      Separator: typeof Lightning.Element;

      Right: {
        Section: typeof SettingsSection;
      };
    };
  };
}
const CONTENT_WIDTH = 1920;
const CONTENT_HEIGHT = 959;
const CONTENT_OFFSET_Y = 120; // Top nav height
const CONTENT_OFFSET_X = 200;

const HEADER_WIDTH = 1520;
const HEADER_FONT_SIZE = 36;

const SETTINGS_WIDTH = 1602;
const SETTINGS_HEIGHT = 750;

const HEADER_OFFSET_Y = 8;
const SETTINGS_OFFSET_Y = 74;

const LEFT_SECTION_WIDTH = 640;

const SECTION_LIST_ITEM_HEIGHT = 72;

const SEPARATOR_WIDTH = 1;
const SEPARATOR_HEIGHT = 750;

const RIGHT_SECTION_WIDTH = 802;
const RIGHT_SECTION_HEIGHT = 872;

const VERTICAL_SPACE_LARGE = 24;

export default class SettingsPage
  extends Page<SettingsPageTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<SettingsPageTemplateSpec>
{
  protected override _pageId = PageId.SETTINGS;

  private isLoggedIn = false;

  private _Content = this.getByRef('Content')!;
  private _Settings = this._Content.getByRef('Settings')!;
  private _Left = this._Settings.getByRef('Left')!;
  private _Right = this._Settings.getByRef('Right')!;
  private _LoggedOutProfile = this._Left.getByRef('LoggedOutProfile')!;
  private _GetStartedButton =
    this._LoggedOutProfile.getByRef('GetStartedButton')!;
  private _LoggedInProfile = this._Left.getByRef('LoggedInProfile')!;
  private _SignOutButtonContainer = this._Left.getByRef('SignOutButton')!;
  private _SignOutButton = this._SignOutButtonContainer.getByRef('Button')!;
  private _SectionsList = this._Left.getByRef('SectionsList')!;
  private _Section = this._Right.getByRef('Section')!;

  get announce() {
    return translate('ttsPrompts.settings');
  }

  static override _template(): Lightning.Component.Template<SettingsPageTemplateSpec> {
    return {
      ...super._template(),
      Content: {
        y: CONTENT_OFFSET_Y,
        w: CONTENT_WIDTH,
        h: CONTENT_HEIGHT,
        Header: {
          type: TextLabel,
          y: HEADER_OFFSET_Y,
          x: CONTENT_OFFSET_X,
          w: HEADER_WIDTH,
          text: {
            text: translate('settings.header'),
            fontSize: HEADER_FONT_SIZE,
            fontFace: getFontFaceFromStyle('bold'),
          },
          flexItem: { marginBottom: VERTICAL_SPACE_LARGE },
        },
        Settings: {
          y: SETTINGS_OFFSET_Y,
          x: CONTENT_OFFSET_X,
          w: SETTINGS_WIDTH,
          h: SETTINGS_HEIGHT,
          flex: {
            direction: 'row',
            justifyContent: 'space-between',
          },
          Left: {
            w: LEFT_SECTION_WIDTH,
            flex: {
              direction: 'column',
            },

            LoggedOutProfile: {
              type: LoggedOutProfile,
              signals: {
                $onHover: '$onHover',
              },
              visible: false,
            },

            LoggedInProfile: {
              type: LoggedInProfile,
              visible: false,
            },

            SectionsList: {
              type: ListWithSpeech,
              itemType: ListSelection,
              direction: 'column',
              signals: { onIndexChanged: true, $onHover: '$onHover' },
              flexItem: { marginBottom: VERTICAL_SPACE_LARGE },
            },

            SignOutButton: {
              type: SignOutButton,
              visible: false,
              signals: {
                $onHover: '$onHover',
                $signOut: '$signOut',
              },
            },
          },

          Separator: {
            rect: true,
            w: SEPARATOR_WIDTH,
            h: SEPARATOR_HEIGHT,
            color: Colors('white').alpha(0.5).get(),
          },

          Right: {
            w: RIGHT_SECTION_WIDTH,
            h: RIGHT_SECTION_HEIGHT,
            Section: {
              type: SettingsSection,
              signals: {
                $onHover: '$onHover',
                $onUnhover: '$onUnhover',
              },
            },
          },
        },
      },
    };
  }

  override _active() {
    super._active();

    this._setState('SectionList');

    this.updateProfiles();
  }

  private updateProfiles() {
    const isLoggedIn = AppData?.loginRadiusService?.isLoggedIn() ?? false;
    this.isLoggedIn = isLoggedIn;
    this._Section.isLoggedIn = isLoggedIn;
    if (isLoggedIn) {
      this._LoggedInProfile.patch({ visible: true });
      this._SignOutButtonContainer.patch({ visible: true });
      this._LoggedOutProfile.patch({ visible: false });
    } else {
      this._LoggedInProfile.patch({ visible: false });
      this._SignOutButtonContainer.patch({ visible: false });
      this._LoggedOutProfile.patch({
        visible: AppData?.loginRadiusService !== null,
      });
    }
    this.setSettingSections();
  }

  private setSettingSections() {
    const settingsSections = SettingsSection.getSettingsSections();

    const items = settingsSections.map(section =>
      SettingsPage.buildListSelection(section),
    );

    this._SectionsList.patch({
      items,
      h: settingsSections.length * SECTION_LIST_ITEM_HEIGHT,
    });

    // sets the initial selection
    const initialIndex = 0;

    this._SectionsList.index = initialIndex;
    this._SectionsList.items[initialIndex].isSelected = true;
    this.updateSelection(initialIndex);
  }

  override _disable() {
    this._SectionsList.items[this._SectionsList.index].isSelected = false;
  }

  private updateList(newIndex: number, oldIndex?: number) {
    if (oldIndex !== undefined) {
      this._SectionsList.items[oldIndex].isSelected = false;
    }
    this._SectionsList.items[newIndex].isSelected = true;
  }

  private updateSelection(newIndex: number) {
    this._Section.selection = newIndex;
  }

  private static buildListSelection(
    sectionId: string,
  ): LightningBuilder<typeof ListSelection, ListSelectionTemplateSpec> {
    const settingsContent = AppData!.config?.login.settings;
    const sectionKey = SettingsPage.mapSectionIdToSettingsKey(sectionId);
    let title = '';

    if (sectionKey && settingsContent) {
      const section = settingsContent[
        sectionKey as keyof typeof settingsContent
      ] as { title?: string } | undefined;
      if (section?.title) {
        title = section.title;
      }
    }

    // Fallback to translations for sections not in API config (e.g., ParentalControls not in config data)
    if (sectionId === 'ShareHistoryWithPlatform' && !title) {
      title = translate(
        `settings.${sectionId}.listTitle`,
        AppData?.device.shareHistoryPlatformName(),
      );
    } else if (!title) {
      title = translate(`settings.${sectionId}.listTitle`);
    }

    return {
      label: title,
      endIcon:
        sectionKey === 'data_center'
          ? 'static/images/privacyoptions-icon.svg'
          : '',
    };
  }

  private static mapSectionIdToSettingsKey(sectionId: string): string | null {
    const mapping: Record<string, string> = {
      Account: 'account',
      PlaybackSettings: 'playback_settings',
      PrivacyLegal: 'privacy_and_legal',
      DataCenter: 'data_center',
      Support: 'support',
    };

    return mapping[sectionId] ?? null;
  }

  private onIndexChanged(indexData: IndexData) {
    const settingsSections = SettingsSection.getSettingsSections();
    if (indexData.dataLength !== settingsSections.length) return;

    this.updateList(indexData.index, indexData.previousIndex);
    this.updateSelection(indexData.index);
  }

  private $onHover(target: Lightning.Element) {
    switch (target) {
      case this._GetStartedButton:
      case this._SignOutButton:
        this._setState('Profile');
        break;
      case this._SectionsList:
        this._setState('SectionList');
        break;
      case this._Section:
        this._setState('Section');
        break;
    }
  }

  private $signOut() {
    AppData?.modalService.open('SignOutModal', {
      data: {
        onConfirm: () => {
          AppData?.loginRadiusService?.signOut().then(() => {
            this.updateLoginState();
            this.updateProfiles();
          });
        },
      },
    });
  }

  static override _states() {
    return [
      class Profile extends this {
        override _getFocused() {
          if (this.isLoggedIn) return this._SignOutButton;
          else return this._GetStartedButton;
        }

        override _handleUp() {
          if (this.isLoggedIn) this._setState('SectionList');
          else super._handleUp();
        }

        override _handleDown() {
          if (!this.isLoggedIn) this._setState('SectionList');
        }
      },
      class SectionList extends this {
        override _getFocused() {
          return this._SectionsList as Lightning.Component;
        }

        override _handleUp() {
          if (this._LoggedOutProfile.visible) this._setState('Profile');
          else super._handleUp();
        }

        override _handleEnter() {
          this._setState('Section');
        }

        override _handleRight() {
          this._setState('Section');
        }

        override _handleDown() {
          if (this.isLoggedIn) this._setState('Profile');
        }
      },
      class Section extends this {
        override _getFocused() {
          return this._Section;
        }

        override _handleLeft() {
          this._setState('SectionList');
        }

        override _handleBack() {
          this._setState('SectionList');
        }
      },
    ];
  }
}
